import React from 'react'
import { Button, DescriptionTooltip, List, ListItem, Row } from '@fs/zion-ui'
import { FormDateSuggest, FormPlaceSuggest, FormTextField } from '@fs/zion-form'
import { NoticeInfo, ContentLock } from '@fs/zion-icon'
import { useTranslation, Trans } from 'react-i18next'
import {
  getNestedOrFloatingElementById,
  getPersonIdsForField,
  getPersonName,
  isDateField,
  isPlaceField,
  shouldLockSharedField,
  useSlsStateContext,
} from '@fs/zion-sls'
import Tip from '../../../../components/tip/Tip'
import HighlightButton from '../highlightButton/HighlightButton'

const ShareableField = ({
  field,
  unlinkingState,
  editHighlightsOverlay,
  handleHighlightButtonClicked,
  hasSelectedHighlights,
  fieldType,
  onFieldValueChange,
  disableUnlinking,
}) => {
  const [unlinking, setUnlinking] = unlinkingState
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()

  const label = field?.displayLabel || field?.label
  const personsIdsSharingField = getPersonIdsForField(field, zionSlsState.images)
  const shouldLock = shouldLockSharedField(field, zionSlsState.images)
  // Get an array of names
  const namesOfPersonsSharingFieldArr = personsIdsSharingField
    ? personsIdsSharingField
        .filter((personId) => personId !== field.personId)
        .map((personId) => {
          const name = getPersonName({ person: getNestedOrFloatingElementById(zionSlsState.images, personId) })
          return name ? ` • ${name}` : ''
        })
    : null
  // Concat the array into a single string which displays an "and more" message if there are too many names to display in 140 characters.
  const andMoreMessage = ` • ${t('family-review.editEntity.and-more', ' and more')}`
  let namesOfPersonsSharingFieldMsg = unlinking
    ? t(
        'family-review.editEntity.changes-to-unlinked-field',
        'Changes to this {fieldType} NO LONGER change the {fieldType} for:',
        {
          fieldType: label,
        }
      )
    : t(
        'family-review.editEntity.changes-to-field',
        'Changes to this {fieldType} will also change the {fieldType} for:',
        {
          fieldType: label,
        }
      )
  let clippedMessage = false
  if (namesOfPersonsSharingFieldArr) {
    namesOfPersonsSharingFieldArr.forEach((name) => {
      if (!clippedMessage) {
        if (namesOfPersonsSharingFieldMsg.length + name.length + andMoreMessage.length > 140) {
          clippedMessage = true
          namesOfPersonsSharingFieldMsg += andMoreMessage
        } else {
          namesOfPersonsSharingFieldMsg += name
        }
      }
    })
  }
  return (
    <>
      {shouldLock ? (
        <List>
          <ListItem
            overline={label}
            primaryText={field?.value}
            endElement={
              <DescriptionTooltip
                target={<ContentLock size="sm" />}
                label={`Locked ${label}`}
                description={t(
                  'family-review.editEntity.locked-description',
                  'To keep records searchable on Family Search, this field will not accept any changes.'
                )}
              />
            }
          />
        </List>
      ) : (
        <>
          {isPlaceField(field) && (
            <FormPlaceSuggest
              data-testid={field?.fieldType || field?.elementType}
              name={field.id}
              key={field.id}
              label={field.label}
              placeholder={field.label}
              standardize
              clearOnClickAway
            />
          )}
          {isDateField(field) && (
            <FormDateSuggest
              data-testid={field?.fieldType || field?.elementType}
              name={field.id}
              key={field.id}
              label={field.label}
              placeholder={field.label}
              standardize
              clearOnClickAway
            />
          )}
          {!isPlaceField(field) && !isDateField(field) && (
            <FormTextField
              label={`${label}`}
              placeholder={label}
              name={field.id}
              maxLength={256}
              data-testid={`edit-${fieldType}-${field.fieldType}`}
              onChange={fieldType === 'event' ? onFieldValueChange : undefined}
            />
          )}
        </>
      )}

      {!shouldLock && namesOfPersonsSharingFieldArr && namesOfPersonsSharingFieldArr.length > 0 && (
        <Row>
          <span growX>
            {unlinking
              ? t('family-review.editEntity.unlinked-field', '{fieldType} unlinked on save.', {
                  fieldType: label,
                })
              : t('family-review.editEntity.linked-field', 'Linked {fieldType}', {
                  fieldType: label,
                })}
          </span>
          <DescriptionTooltip
            target={<NoticeInfo size="sm" />}
            label={t('family-review.editEntity.linked-field', 'Linked {fieldType}', {
              fieldType: label,
            })}
            description={namesOfPersonsSharingFieldMsg}
          />
        </Row>
      )}
      {!shouldLock && (
        <HighlightButton
          isAdding={!hasSelectedHighlights(field.id)}
          editHighlightsOverlay={editHighlightsOverlay}
          id={field.id}
          handleHighlightButtonClicked={handleHighlightButtonClicked}
        />
      )}
      {!shouldLock && namesOfPersonsSharingFieldArr && namesOfPersonsSharingFieldArr.length > 0 && (
        <Tip
          primaryText={t('family-review.editEntity.what-is-a-linked-field', 'What is a linked {fieldType}?', {
            fieldType: label,
          })}
          listPrimaryText={
            <>
              <p>
                {t(
                  'family-review.editEntity.primary-text',
                  'A linked {fieldType} is a {fieldType} that is shared with other record names.',
                  {
                    fieldType: label,
                  }
                )}
              </p>
              <br />
              {!disableUnlinking && (
                <>
                  <Trans i18nKey="family-review.editEntity.linked-field-tip-instructions">
                    <span>
                      <strong>Select</strong> the Unlink {{ fieldType: label }} button to remove the link.
                    </span>
                  </Trans>
                  <Button
                    onClick={() => setUnlinking(!unlinking)}
                    data-testid={`toggle-unlink-${label.toLowerCase()}-button`}
                    linkName={unlinking ? `Shareable Field Relink Field` : `Shareable Field Unlink Field`}
                  >
                    {unlinking
                      ? t('family-review.editEntity.relink-field', 'Relink {fieldType}', {
                          fieldType: label,
                        })
                      : t('family-review.editEntity.unlink-field', 'Unlink {fieldType}', {
                          fieldType: label,
                        })}
                  </Button>
                </>
              )}
            </>
          }
        />
      )}
    </>
  )
}

export default ShareableField
